import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useQuiz } from "../contexts/QuizContext";
import { toast } from "react-toastify";
import trophyIcon from "/assets/images/trophy.png";
import shareGeneric from "/assets/images/share-generic.png";
import instagramIcon from "/assets/images/insta.png";
import whatsappIcon from "/assets/images/whatsapp.png";
import snapchatIcon from "/assets/images/snap.png";
import facebookIcon from "/assets/images/fb.png";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

export const MyGoals = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [todayGoalProgress, setTodayGoalProgress] = useState(0);
  const [weeklyGoalProgress, setWeeklyGoalProgress] = useState(0);
  const [todaysGoals, setTodaysGoals] = useState([]);
  const [weeklyGoals, setWeeklyGoals] = useState([]);
  const [startingGoalId, setStartingGoalId] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { saveQuizState } = useQuiz();
  const hasFetchedRef = useRef(false);
  const [showSharePopover, setShowSharePopover] = useState(null);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");

  // Check if we're on the nested goals route
  const isOnGoalsRoute = location.pathname.endsWith("/goals");

  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const GOAL_RATE_LIMIT_SUPPORT_MESSAGE =
    "You've reached the current limit for goal quizzes. Please wait a few minutes before trying again or contact support if you need a higher limit.";
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  // Refresh data when returning from goal creation
  useEffect(() => {
    console.log("MyGoals: window.goalsData", window.goalsData);
    console.log("MyGoals: isOnGoalsRoute", isOnGoalsRoute);
    if (!isOnGoalsRoute && window.goalsData === null) {
      // Force refresh when returning from goal creation
      const token = localStorage.getItem("token");
      if (!token) return;

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const fetchGoals = async () => {
        try {
          const response = await axios.get(`${baseUrl}/v2/get-goals`, {
            headers,
          });
          const goals = response.data.goals || [];
          window.goalsData = goals;
          processGoalsData(goals);
        } catch (error) {
          console.error("Error refreshing goals:", error);
        }
      };

      const processGoalsData = (goals) => {
        // Process goals data in a single pass
        // Use local timezone - dayjs uses local time by default
        const today = dayjs().startOf("day");
        const tomorrow = today.add(1, "day");
        const nextWeek = today.add(7, "day");
        const todayList = [];
        const weekList = [];
        let todayCompleted = 0;
        let weekCompleted = 0;

        goals.forEach((goal) => {
          // Parse testDate in local timezone - dayjs parses date strings in local time by default
          const testDate = dayjs(goal.testDate).startOf("day");

          // Check for today's goals:
          // 1. Goals due today (testDate is same as today)
          // 2. Goals with "1 Day Before" reminder that are due tomorrow
          const isDueToday = testDate.isSame(today, "day");
          const isReminderForTomorrow =
            goal.reminder === "1 Day Before" &&
            testDate.isSame(tomorrow, "day");

          if (isDueToday || isReminderForTomorrow) {
            todayList.push(goal);
            if (goal.completed) todayCompleted++;
          }
          // Check for weekly goals (between tomorrow and next week, excluding today's goals)
          else if (
            testDate.isAfter(tomorrow, "day") &&
            testDate.isSameOrBefore(nextWeek, "day")
          ) {
            weekList.push(goal);
            if (goal.completed) weekCompleted++;
          }
        });

        // Calculate progress percentages
        const todayProgress = todayList.length
          ? (todayCompleted / todayList.length) * 100
          : 0;
        const weekProgress = weekList.length
          ? (weekCompleted / weekList.length) * 100
          : 0;

        // Update all states at once
        setTodayGoalProgress(todayProgress);
        setWeeklyGoalProgress(weekProgress);
        setTodaysGoals(todayList);
        setWeeklyGoals(weekList);
      };

      fetchGoals();
    }
  }, [isOnGoalsRoute, baseUrl]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      setIsLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const fetchGoals = async () => {
      console.log("MyGoals: Fetching goals API...");
      setIsLoading(true);
      try {
        const response = await axios.get(`${baseUrl}/v2/get-goals`, {
          headers,
        });
        const goals = response.data.goals || [];
        console.log(
          "MyGoals: Goals fetched successfully:",
          goals.length,
          "goals"
        );

        // Store goals data globally for other components to use
        window.goalsData = goals;

        processGoalsData(goals);
        setError(null);
      } catch (error) {
        console.error("Error fetching goals:", error);
        setError("Failed to load goals. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    const processGoalsData = (goals) => {
      // Process goals data in a single pass
      // Use local timezone - dayjs uses local time by default
      const today = dayjs().startOf("day");
      const tomorrow = today.add(1, "day");
      const nextWeek = today.add(7, "day");
      const todayList = [];
      const weekList = [];
      let todayCompleted = 0;
      let weekCompleted = 0;

      goals.forEach((goal) => {
        // Parse testDate in local timezone - dayjs parses date strings in local time by default
        const testDate = dayjs(goal.testDate).startOf("day");

        // Check for today's goals:
        // 1. Goals due today (testDate is same as today)
        // 2. Goals with "1 Day Before" reminder that are due tomorrow
        const isDueToday = testDate.isSame(today, "day");
        const isReminderForTomorrow =
          goal.reminder === "1 Day Before" && testDate.isSame(tomorrow, "day");

        if (isDueToday || isReminderForTomorrow) {
          todayList.push(goal);
          if (goal.completed) todayCompleted++;
        }
        // Check for weekly goals (between tomorrow and next week, excluding today's goals)
        else if (
          testDate.isAfter(tomorrow, "day") &&
          testDate.isSameOrBefore(nextWeek, "day")
        ) {
          weekList.push(goal);
          if (goal.completed) weekCompleted++;
        }
      });

      // Calculate progress percentages
      const todayProgress = todayList.length
        ? (todayCompleted / todayList.length) * 100
        : 0;
      const weekProgress = weekList.length
        ? (weekCompleted / weekList.length) * 100
        : 0;

      // Update all states at once
      setTodayGoalProgress(todayProgress);
      setWeeklyGoalProgress(weekProgress);
      setTodaysGoals(todayList);
      setWeeklyGoals(weekList);
    };

    // Check for cached data first, but don't wait too long
    if (window.goalsData && window.goalsData.length > 0) {
      console.log("MyGoals: Using cached goals data");
      processGoalsData(window.goalsData);
      setIsLoading(false);
    } else {
      // Make API call immediately
      fetchGoals();
    }
  }, [baseUrl]);

  const handleStartGoal = async (goal) => {
    const goalId = goal?._id || goal?.id || goal;
    if (!goalId) {
      console.error("handleStartGoal: Missing goal identifier", goal);
      setError("Unable to start this goal. Please try again.");
      return;
    }

    try {
      setStartingGoalId(goalId);
      setError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required.");
        return;
      }

      const response = await fetch(
        `${baseUrl}/api/v2/start-goal-quiz/${goalId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        const normalizedMessage =
          rateLimitMessage?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        setError(normalizedMessage);
        showRateLimitModal(normalizedMessage);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText?.trim() || "Failed to start quiz. Please try again."
        );
      }

      const rawPayload = await response.json();
      const resultPayload =
        rawPayload && typeof rawPayload === "object" && rawPayload.result
          ? rawPayload.result
          : rawPayload;

      const rawQuestionList = Array.isArray(resultPayload?.questions)
        ? resultPayload.questions
        : [];

      if (rawQuestionList.length === 0) {
        setError("No questions available for this goal.");
        return;
      }

      // Normalize questions: convert option objects to strings
      const normalizeOption = (option) => {
        if (typeof option === "string") return option;
        if (option && typeof option === "object") {
          return (
            option.option_text ||
            option.text ||
            option.label ||
            option.value ||
            String(option)
          );
        }
        return String(option || "");
      };

      const questionList = rawQuestionList.map((question) => {
        const normalizedQuestion = { ...question };

        // Normalize options array if it exists
        if (Array.isArray(normalizedQuestion.options)) {
          normalizedQuestion.options =
            normalizedQuestion.options.map(normalizeOption);
        }

        // Normalize choices array if it exists (some APIs use 'choices' instead of 'options')
        if (Array.isArray(normalizedQuestion.choices)) {
          normalizedQuestion.choices =
            normalizedQuestion.choices.map(normalizeOption);
        }

        return normalizedQuestion;
      });

      const questionCount = Number.isFinite(
        Number(resultPayload?.question_count)
      )
        ? Math.max(Number(resultPayload.question_count), questionList.length)
        : questionList.length;

      const timerSeconds =
        Number.isFinite(Number(resultPayload?.timer)) &&
        Number(resultPayload.timer) > 0
          ? Number(resultPayload.timer)
          : questionCount * 60;

      const userAnswersSeed = Array.from({ length: questionCount }, () => null);

      const normalizedResult = {
        ...resultPayload,
        questions: questionList,
        question_count: questionCount,
        timer: timerSeconds,
      };

      const storedQuestions =
        rawPayload && typeof rawPayload === "object" && rawPayload.result
          ? { ...rawPayload, result: normalizedResult }
          : normalizedResult;

      const goalDetailsPayload = {
        subject:
          goal?.subject ??
          normalizedResult?.subject ??
          normalizedResult?.extracted_content?.subject ??
          "",
        topic:
          goal?.topic ??
          normalizedResult?.topic ??
          normalizedResult?.extracted_content?.topic ??
          "",
        testDate: goal?.testDate ?? null,
        reminder: goal?.reminder ?? null,
        goalId,
      };

      saveQuizState({
        topicId: goalId,
        questions: storedQuestions,
        goalDetails: goalDetailsPayload,
        currentQuestionIndex: 0,
        selectedOption: null,
        score: 0,
        showExplanation: false,
        quizCompleted: false,
        leveledUp: false,
        goalStatus: goal?.status ?? "",
        userAnswers: userAnswersSeed,
      });

      navigate(`/practice/goalquestions/${goalId}`, {
        state: {
          attemptId: normalizedResult?.attempt_id,
          goalId,
        },
      });
    } catch (error) {
      console.error("Error starting quiz:", error);
      const fallbackMessage =
        error?.message || "Failed to start quiz. Please try again.";
      setError(fallbackMessage);
    } finally {
      setStartingGoalId(null);
    }
  };

  const handleSetNewGoal = () => {
    navigate("/practice/mygoals/goals");
  };

  // Helper to generate share URLs
  const getShareUrl = (platform, goal) => {
    const text = encodeURIComponent(
      `I completed my goal: ${goal.subject} - ${goal.topic}!`
    );
    const url = encodeURIComponent(window.location.href);
    switch (platform) {
      case "instagram":
        return `https://www.instagram.com/?text=${text}`;
      case "whatsapp":
        return `https://wa.me/?text=${text}%20${url}`;
      case "snapchat":
        return `https://www.snapchat.com/`; // Snapchat sharing is limited
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
      default:
        return `mailto:?subject=I completed my goal!&body=${text}%20${url}`;
    }
  };

  const rateLimitModal = (
    <RateLimitModal
      isOpen={isRateLimitModalOpen}
      serverMessage={rateLimitServerMessage}
      supportMessage={GOAL_RATE_LIMIT_SUPPORT_MESSAGE}
      onClose={handleRateLimitModalClose}
    />
  );

  if (isLoading) {
    return (
      <>
        <div className="bg-blue-50 p-4 sm:p-6 rounded-xl min-h-[200px] flex items-center justify-center">
          <div className="text-gray-600">Loading goals...</div>
        </div>
        {rateLimitModal}
      </>
    );
  }

  // if (error) {
  //   return (
  //     <>
  //       <div className="bg-blue-50 p-4 sm:p-6 rounded-xl">
  //         <div className="text-red-600 text-center">{error}</div>
  //       </div>
  //       {rateLimitModal}
  //     </>
  //   );
  // }

  // If we're on the nested goals route, only render the outlet
  if (isOnGoalsRoute) {
    return (
      <>
        <Outlet />
        {rateLimitModal}
      </>
    );
  }

  // Rest of the component remains unchanged
  return (
    <>
      <div className="bg-blue-50 p-4 sm:p-6 rounded-xl">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleSetNewGoal}
            className="bg-[#0D99FF] hover:bg-blue-600 text-white font-semibold py-2 sm:py-3 px-8 sm:px-12 rounded-full shadow-lg text-sm sm:text-base transition-all duration-200"
          >
            Set New Goal
          </button>
        </div>

        {/* Today's Goals */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base sm:text-lg font-semibold">Today's Goal</h2>
          </div>
          <div className="relative h-3 bg-gray-200 rounded-full mb-1">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"
              style={{ width: `${todayGoalProgress}%` }}
            ></div>
          </div>
          <div className="text-right text-xs sm:text-sm text-gray-600">
            {todayGoalProgress.toFixed(0)}% Complete
          </div>
        </div>

        <div className="space-y-3 mb-8">
          {todaysGoals.map((goal, index) => (
            <div
              key={goal._id || index}
              className={`bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:justify-between ${
                goal.completed ? "border-2 border-green-400" : ""
              }`}
            >
              <div>
                <h3 className="font-semibold text-sm sm:text-base">
                  {goal.subject} - {goal.topic}
                </h3>
                {goal.completed ? (
                  <div className="text-green-600 text-xs sm:text-sm">
                    Completed on{" "}
                    {new Date(goal.completed_at).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </div>
                ) : (
                  <>
                    {(dayjs(goal.testDate)
                      .startOf("day")
                      .isSame(dayjs().startOf("day"), "day") ||
                      dayjs(goal.testDate)
                        .startOf("day")
                        .isSame(
                          dayjs().add(1, "day").startOf("day"),
                          "day"
                        )) && (
                      <h1 className="text-sm sm:text-base text-amber-500 mb-0">
                        {dayjs(goal.testDate)
                          .startOf("day")
                          .isSame(dayjs().startOf("day"), "day")
                          ? "Due by Today"
                          : "Due by Tomorrow"}
                      </h1>
                    )}
                    <p className="text-xs text-gray-400 mt-0">
                      Reminder: {goal.reminder}
                    </p>
                  </>
                )}
              </div>
              <div className="flex mt-2 sm:mt-0 items-center gap-2">
                {goal.completed ? (
                  <>
                    <img
                      src={trophyIcon}
                      alt="Trophy"
                      className="w-7 h-7 mr-1"
                    />
                    <button className="px-4 py-1 bg-emerald-500 text-white rounded-full text-xs sm:text-sm min-w-[120px] text-center flex items-center justify-center gap-1 font-bold">
                      Completed
                    </button>
                    <div className="relative">
                      <button
                        className="px-5 py-1 rounded-full text-xs sm:text-sm border border-gray-300 hover:bg-gray-100 text-center"
                        onClick={() =>
                          setShowSharePopover(
                            showSharePopover === index ? null : index
                          )
                        }
                      >
                        Share
                      </button>
                      {showSharePopover === index && (
                        <div className="absolute top-12 right-0 bg-white shadow-xl rounded-xl p-4 flex gap-4 z-50 border border-gray-200 min-w-[220px]">
                          <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
                          <a
                            href={getShareUrl("generic", goal)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={shareGeneric}
                              className="w-7 h-7 hover:scale-110 transition-transform"
                            />
                          </a>
                          <a
                            href={getShareUrl("instagram", goal)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={instagramIcon}
                              className="w-7 h-7 hover:scale-110 transition-transform"
                            />
                          </a>
                          <a
                            href={getShareUrl("whatsapp", goal)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={whatsappIcon}
                              className="w-7 h-7 hover:scale-110 transition-transform"
                            />
                          </a>
                          <a
                            href={getShareUrl("snapchat", goal)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={snapchatIcon}
                              className="w-7 h-7 hover:scale-110 transition-transform"
                            />
                          </a>
                          <a
                            href={getShareUrl("facebook", goal)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={facebookIcon}
                              className="w-7 h-7 hover:scale-110 transition-transform"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <button
                    className={`px-4 py-1 rounded-full text-xs sm:text-sm text-white transition-colors min-w-[120px] text-center ${
                      startingGoalId === goal._id
                        ? "bg-[#0D99FF] cursor-not-allowed"
                        : "bg-[#0D99FF] hover:bg-blue-600"
                    }`}
                    onClick={() => handleStartGoal(goal)}
                    disabled={startingGoalId === goal._id}
                  >
                    {startingGoalId === goal._id ? "Preparing..." : "Start"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Weekly Goals */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
            <h2 className="text-base sm:text-lg font-semibold">
              This Week's Goal
            </h2>
            <button className="mt-1 sm:mt-0 px-3 py-1 rounded-full text-xs sm:text-sm border border-gray-300 bg-white hover:bg-gray-50">
              Share weekly progress
            </button>
          </div>
          <div className="relative h-3 bg-gray-200 rounded-full mb-1">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"
              style={{ width: `${weeklyGoalProgress}%` }}
            ></div>
          </div>
          <div className="text-right text-xs sm:text-sm text-gray-600">
            {weeklyGoalProgress.toFixed(0)}% Complete
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {weeklyGoals.map((goal, index) => (
            <div
              key={goal._id || index}
              className={`bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:justify-between ${
                goal.completed ? "border-2 border-green-400" : ""
              }`}
            >
              <div>
                <h3 className="font-semibold text-xs sm:text-sm">
                  {dayjs(goal.testDate).format("dddd")} - {goal.subject} -{" "}
                  {goal.topic}
                </h3>
                {goal.completed ? (
                  <div className="text-green-600 text-xs sm:text-sm">
                    Completed on{" "}
                    {new Date(goal.completed_at).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </div>
                ) : (
                  <>
                    <div className="text-sm sm:text-sm text-amber-500 mb-0">
                      {goal.details}
                    </div>
                    <div className="text-xs text-amber-500 mb-0 font-medium mb-0">
                      Due date:{" "}
                      {dayjs(goal.testDate).format("dddd, MMM D, YYYY")}
                    </div>
                    <p className="text-xs text-gray-400">
                      Reminder: {goal.reminder}
                    </p>
                  </>
                )}
              </div>
              {goal.completed ? (
                <div className="flex mt-2 sm:mt-0 items-center gap-2">
                  <img src={trophyIcon} alt="Trophy" className="w-7 h-7 mr-1" />
                  <button className="px-4 py-1 bg-green-400 text-white rounded-full text-xs sm:text-sm min-w-[120px] text-center flex items-center justify-center gap-1">
                    Completed
                  </button>
                  <div className="relative">
                    <button
                      className="px-5 py-1 rounded-full text-xs sm:text-sm border border-gray-300 hover:bg-gray-100 text-center"
                      onClick={() =>
                        setShowSharePopover(
                          showSharePopover === index ? null : index
                        )
                      }
                    >
                      Share
                    </button>
                    {showSharePopover === index && (
                      <div className="absolute top-12 right-0 bg-white shadow-xl rounded-xl p-4 flex gap-4 z-50 border border-gray-200 min-w-[220px]">
                        <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
                        <a
                          href={getShareUrl("generic", goal)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={shareGeneric}
                            className="w-7 h-7 hover:scale-110 transition-transform"
                          />
                        </a>
                        <a
                          href={getShareUrl("instagram", goal)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={instagramIcon}
                            className="w-7 h-7 hover:scale-110 transition-transform"
                          />
                        </a>
                        <a
                          href={getShareUrl("whatsapp", goal)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={whatsappIcon}
                            className="w-7 h-7 hover:scale-110 transition-transform"
                          />
                        </a>
                        <a
                          href={getShareUrl("snapchat", goal)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={snapchatIcon}
                            className="w-7 h-7 hover:scale-110 transition-transform"
                          />
                        </a>
                        <a
                          href={getShareUrl("facebook", goal)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={facebookIcon}
                            className="w-7 h-7 hover:scale-110 transition-transform"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex mt-2 sm:mt-0 items-center gap-2">
                  <button
                    className={`px-9 py-1 rounded-full text-xs sm:text-sm text-white transition-colors min-w-[120px] text-center ${
                      startingGoalId === goal._id
                        ? "bg-[#0D99FF] cursor-not-allowed"
                        : "bg-[#0D99FF] hover:bg-blue-600"
                    }`}
                    onClick={() => handleStartGoal(goal)}
                    disabled={startingGoalId === goal._id}
                  >
                    {startingGoalId === goal._id ? "Preparing..." : "Start"}
                  </button>
                  <button className="px-5 py-1 rounded-full text-xs sm:text-sm border border-gray-300 hover:bg-gray-100 text-center">
                    Share
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-6">
          <button
            onClick={handleSetNewGoal}
            className="bg-[#0D99FF] hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 sm:py-3 px-8 sm:px-12 rounded-full shadow-lg text-sm sm:text-base"
          >
            Set New Goal
          </button>
        </div>

        {/* Render nested routes */}
        <Outlet />
      </div>
      {rateLimitModal}
    </>
  );
};

export default MyGoals;
